import React, { useCallback, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseFormModal } from 'components/enterprise-shell';
import { SyutSelect } from 'components/ui';
import type {
  AppendTaxMasterVersionRequest,
  CreateTaxMasterRequest,
  TaxMasterApi,
  TaxMasterBand,
  TaxMasterBasisOption,
  TaxMasterSummary,
  TaxMasterVersion,
} from './taxMaster.types';
import './tax-master.css';

/**
 * ============================================================================
 * TAX MASTER FORM MODAL — relocatable shared module (ADR-0019 §8)
 * ============================================================================
 * The master/version editor on the canonical `EnterpriseFormModal`: fixed-
 * per-mode header, save confirmation, loading overlay, unsaved-changes guard on
 * close, minimize-to-bar, and inline field errors.
 *
 * Four editor modes over the wrapper's three:
 *   create  → wrapper 'create'   new master + first version
 *   copy    → wrapper 'copy'     new master prefilled from another master's latest version
 *   append  → wrapper 'create'   new version of an existing master (verb "New")
 *   replace → wrapper 'edit'     amend a SCHEDULED (future) version
 *
 * The SERVER still owns every invariant (the API template Tax module). Validation here is
 * convenience only, and a server refusal is shown verbatim AND persistently —
 * the wrapper's toast expires, and the refusal text is the instruction for what to fix.
 *
 * Slab bands are entered as UPPER LIMITS only; each band's start is derived from
 * the one below it, so a gap cannot be expressed. The server enforces the same
 * rule (ADR-0020) — this just stops the user reaching a refusal.
 * ============================================================================
 */

export type TaxEditorMode = 'create' | 'copy' | 'append' | 'replace';

interface BandDraft {
  valueFrom: string;
  valueTo: string;
  rates: Record<string, string>;
}

export interface TaxMasterFormState {
  rateCode: string;
  name: string;
  supplyCategory: string;
  effectiveFrom: string;
  isSlab: boolean;
  withCess: boolean;
  conditionKey: string;
  notificationReference: string;
  reason: string;
  bands: BandDraft[];
}

const emptyBand = (codes: string[]): BandDraft => ({
  valueFrom: '',
  valueTo: '',
  rates: Object.fromEntries(codes.map((c) => [c, ''])),
});

/** Categories whose rate is nil by definition — rates are not the user's to set. */
const NIL_CATEGORIES = ['EXEMPT', 'ZERO'];

/**
 * Re-derives every band's start so the ladder is contiguous by construction: band 1 starts at 0,
 * band N starts exactly where band N-1 ends. Users only ever type the upper bounds, which is how
 * the law states them anyway ("up to ₹7,500", "above ₹7,500").
 *
 * Bands MEET rather than leaving a step between them (TaxCore ADR-0020). The endpoint is therefore
 * in both bands, and the engine resolves it to the LOWER one.
 */
const chainBands = (bands: BandDraft[]): BandDraft[] => bands.map((b, i) => {
  if (i === 0) return { ...b, valueFrom: '0' };
  const prevTo = bands[i - 1].valueTo;
  if (prevTo === '' || Number.isNaN(Number(prevTo))) return { ...b, valueFrom: '' };
  return { ...b, valueFrom: prevTo };
});

const toDraft = (band: TaxMasterBand, codes: string[]): BandDraft => ({
  valueFrom: band.valueFrom?.toString() ?? '',
  valueTo: band.valueTo?.toString() ?? '',
  rates: Object.fromEntries(codes.map((c) => [
    c, band.components.find((x) => x.componentCode === c)?.ratePercentage.toString() ?? '',
  ])),
});

export interface TaxMasterFormModalProps {
  isOpen: boolean;
  editorMode: TaxEditorMode;
  api: TaxMasterApi;
  jurisdictionCode: string;
  componentCodes: string[];
  /** Published slab bases (from the pack). Free text is not accepted: a basis the host cannot supply is refused server-side. */
  bases?: TaxMasterBasisOption[];
  /**
   * Supply categories offerable in this jurisdiction.
   *
   * ZERO is deliberately NOT universal. Under GST, zero-rating is a property of the SUPPLY
   * (export / SEZ) and is asserted on the document, where it short-circuits before any master is
   * consulted — so a zero-rated master would wrongly pin an item to nil regardless of where it is
   * sold. Under Botswana VAT it IS a rate family (`BW_VAT_ZERO`), so the option must remain
   * available there. The host decides; the default is the conservative pair.
   */
  supplyCategories?: Array<{ value: string; label: string }>;
  /** The master this version belongs to (append / replace) or is copied from (copy). */
  rateCode?: string | null;
  /** Prefill source: latest version for append / copy, the target version for replace. */
  baseVersion?: TaxMasterVersion | null;
  /** The master's summary — name and category for copy; the strip label elsewhere. */
  baseSummary?: TaxMasterSummary | null;
  tabId?: string;
  moduleName?: string;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onClose: () => void;
  onSaved: () => void;
  onRestoreModal: (mode: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => void;
}

const DEFAULT_SUPPLY_CATEGORIES = [
  { value: 'STANDARD', label: 'Standard' },
  { value: 'EXEMPT', label: 'Nil / Exempt' },
];

export const TaxMasterFormModal: React.FC<TaxMasterFormModalProps> = ({
  isOpen, editorMode, api, jurisdictionCode, componentCodes, bases = [], supplyCategories, rateCode, baseVersion,
  baseSummary, tabId, moduleName = 'Tax Masters', createInstanceId, minimizeRef, onClose, onSaved, onRestoreModal,
}) => {
  const categories = supplyCategories ?? DEFAULT_SUPPLY_CATEGORIES;
  const isNewMaster = editorMode === 'create' || editorMode === 'copy';
  // Held outside the form so a refusal survives until the next submit, unlike the wrapper's toast.
  const [serverError, setServerError] = useState<string | null>(null);

  const emptyForm: TaxMasterFormState = {
    rateCode: '',
    name: '',
    supplyCategory: 'STANDARD',
    effectiveFrom: '',
    isSlab: false,
    withCess: false,
    conditionKey: '',
    notificationReference: '',
    reason: '',
    bands: [emptyBand(componentCodes)],
  };

  const populateOnOpen = useCallback((): TaxMasterFormState => {
    setServerError(null);

    if (!baseVersion) {
      return { ...emptyForm, bands: [emptyBand(componentCodes)] };
    }

    return {
      ...emptyForm,
      // Copy: a NEW master — the code must be the user's; the name and category carry over.
      rateCode: editorMode === 'copy' ? '' : (rateCode ?? ''),
      name: editorMode === 'copy' ? (baseSummary?.name ?? '') : '',
      supplyCategory: editorMode === 'copy' ? (baseSummary?.supplyCategory ?? 'STANDARD') : 'STANDARD',
      // Amending a future version edits that version's own date; a NEW version or a copy must
      // not inherit the date it is meant to supersede.
      effectiveFrom: editorMode === 'replace' ? baseVersion.effectiveFrom.slice(0, 10) : '',
      isSlab: baseVersion.bands.length > 1 || !!baseVersion.conditionKey,
      withCess: baseVersion.bands.some((b) => b.components.some((c) => c.componentCode === 'CESS')),
      conditionKey: baseVersion.conditionKey ?? '',
      // Re-derive the starts on load too: an existing ladder may have been saved with a gap
      // before this rule existed, and editing it should close the gap rather than carry it.
      bands: chainBands(baseVersion.bands.map((b) => toDraft(b, componentCodes))),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseVersion, baseSummary, componentCodes, editorMode, rateCode]);

  const activeCodes = useCallback(
    (form: TaxMasterFormState) => componentCodes.filter((c) => c !== 'CESS' || form.withCess),
    [componentCodes]);

  const validate = useCallback((form: TaxMasterFormState): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!form.effectiveFrom) errors.effectiveFrom = 'Effective date is required.';

    if (isNewMaster) {
      if (!form.rateCode.trim()) errors.rateCode = 'Code is required.';
      if (!form.name.trim()) errors.name = 'Name is required.';
    }

    if (form.isSlab && !form.conditionKey.trim()) {
      errors.conditionKey = 'A slab must name the published basis its bands evaluate.';
    }

    const codes = activeCodes(form);
    const priced = form.bands.some((b) => codes.some((c) => b.rates[c] !== ''));
    if (!priced) errors.bands = 'Enter at least one component rate.';

    if (form.isSlab) {
      // Starts are derived, never typed, so the only thing that can be wrong is an upper bound.
      form.bands.forEach((b, i) => {
        const isLast = i === form.bands.length - 1;
        if (!isLast && b.valueTo === '') {
          errors.bands = `Band ${i + 1} must have an upper limit — only the last band may be open-ended.`;
        }
        if (b.valueTo !== '' && Number(b.valueTo) < Number(b.valueFrom || 0)) {
          errors.bands = `Band ${i + 1}: the upper limit is below where the band starts.`;
        }
      });
    }

    return errors;
  }, [activeCodes, isNewMaster]);

  const buildBands = useCallback((form: TaxMasterFormState): TaxMasterBand[] => {
    const codes = activeCodes(form);
    return form.bands.map((b) => ({
      valueFrom: form.isSlab ? Number(b.valueFrom) : null,
      valueTo: form.isSlab && b.valueTo !== '' ? Number(b.valueTo) : null,
      components: codes
        .filter((c) => b.rates[c] !== '')
        .map((c) => ({ componentCode: c, ratePercentage: Number(b.rates[c]) })),
    }));
  }, [activeCodes]);

  const onSave = useCallback(async (form: TaxMasterFormState) => {
    setServerError(null);
    try {
      if (isNewMaster) {
        const request: CreateTaxMasterRequest = {
          rateCode: form.rateCode.trim(),
          name: form.name.trim(),
          supplyCategory: form.supplyCategory,
          effectiveFrom: form.effectiveFrom,
          conditionKey: form.isSlab ? form.conditionKey.trim() : null,
          bands: buildBands(form),
          notificationReference: form.notificationReference.trim() || null,
        };
        await api.create(jurisdictionCode, request);
        return;
      }

      const request: AppendTaxMasterVersionRequest = {
        effectiveFrom: form.effectiveFrom,
        bands: buildBands(form),
        notificationReference: form.notificationReference.trim() || null,
        reason: form.reason.trim() || null,
      };

      if (editorMode === 'replace' && rateCode && baseVersion) {
        await api.replaceVersion(jurisdictionCode, rateCode, baseVersion.effectiveFrom, request);
      } else if (rateCode) {
        await api.appendVersion(jurisdictionCode, rateCode, request);
      }
    } catch (err) {
      // Keep the refusal on screen, then rethrow so the wrapper toasts it and holds the modal open.
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setServerError(e?.response?.data?.message || e?.message || 'Save failed.');
      throw err;
    }
  }, [api, baseVersion, buildBands, editorMode, isNewMaster, jurisdictionCode, rateCode]);

  const noun = isNewMaster ? 'Tax Master' : 'Version';
  const wrapperMode = editorMode === 'replace' ? 'edit' : editorMode === 'copy' ? 'copy' : 'create';
  const basisOptions = bases.map((b) => ({ value: b.value, label: b.description ? `${b.label} — ${b.description}` : b.label }));

  return (
    <EnterpriseFormModal<TaxMasterFormState, TaxMasterVersion>
      isOpen={isOpen}
      mode={wrapperMode}
      record={baseVersion ?? null}
      noun={noun}
      createVerb={editorMode === 'append' ? 'New' : 'Add New'}
      size="lg"
      emptyForm={emptyForm}
      populateOnOpen={populateOnOpen}
      validate={validate}
      onSave={onSave}
      confirmSaveMessage={() => (isNewMaster
        ? 'Create this tax master and its first version?'
        : 'Save this version? Rates apply to documents dated on or after its effective date.')}
      successMessage={() => (isNewMaster ? 'Tax master created.' : 'Version saved.')}
      tabId={tabId}
      moduleName={moduleName}
      createInstanceId={createInstanceId}
      recordKey={rateCode ?? undefined}
      getStripFormLabel={(f) => f.rateCode || f.name || undefined}
      getStripRecordLabel={() => rateCode ?? undefined}
      minimizeRef={minimizeRef}
      onClose={onClose}
      onSaved={onSaved}
      onRestoreModal={onRestoreModal}
    >
      {({ form, setForm, getFieldError, clearFieldError }) => {
        const codes = activeCodes(form);
        const isNil = NIL_CATEGORIES.includes(form.supplyCategory);
        const bandCols = (form.isSlab ? 2 : 0) + codes.length;

        /** Nil/Exempt has no rate to set — force zeros rather than leaving stale figures behind. */
        const applyCategory = (value: string) => {
          clearFieldError('bands');
          setForm((f) => {
            const nil = NIL_CATEGORIES.includes(value);
            if (!nil) return { ...f, supplyCategory: value };
            return {
              ...f,
              supplyCategory: value,
              // A nil rate cannot vary by value, so a slab is meaningless here.
              isSlab: false,
              conditionKey: '',
              bands: [{
                valueFrom: '',
                valueTo: '',
                // The server requires every band to price at least one component, so nil is
                // expressed as an explicit 0 — not as an absent rate.
                rates: Object.fromEntries(componentCodes.map((c) => [c, '0'])),
              }],
            };
          });
        };

        const setBand = (index: number, patch: Partial<BandDraft>) => {
          clearFieldError('bands');
          setForm((f) => ({
            ...f,
            bands: chainBands(f.bands.map((b, i) => (i === index ? { ...b, ...patch } : b))),
          }));
        };

        const setRate = (index: number, code: string, value: string) => {
          clearFieldError('bands');
          setForm((f) => ({
            ...f,
            bands: f.bands.map((b, i) => {
              if (i !== index) return b;
              const rates = { ...b.rates, [code]: value };
              // Typing IGST prefills the intra halves — the user's assertion, freely overridable.
              if (code === 'IGST' && value !== '' && !Number.isNaN(Number(value))) {
                const half = (Number(value) / 2).toString();
                const prevHalf = (Number(b.rates['IGST'] || 0) / 2).toString();
                if (b.rates['CGST'] === '' || b.rates['CGST'] === prevHalf) rates['CGST'] = half;
                if (b.rates['SGST'] === '' || b.rates['SGST'] === prevHalf) rates['SGST'] = half;
              }
              return { ...b, rates };
            }),
          }));
        };

        const removeBand = (index: number) => {
          clearFieldError('bands');
          setForm((f) => ({ ...f, bands: chainBands(f.bands.filter((_, x) => x !== index)) }));
        };

        const addBand = () => {
          clearFieldError('bands');
          setForm((f) => ({ ...f, bands: chainBands([...f.bands, emptyBand(componentCodes)]) }));
        };

        return (
          <>
            {serverError && (
              <div role="alert" className="txm-server-error">{serverError}</div>
            )}

            {/* Row 1 — identity (new master only) */}
            {isNewMaster && (
              <div className="form-grid-3">
                <div className="form-group" data-field="rateCode">
                  <label className="form-label">Code <span className="text-danger">*</span></label>
                  <input
                    className={`form-input${getFieldError('rateCode') ? ' form-field-error' : ''}`}
                    value={form.rateCode}
                    maxLength={30}
                    placeholder="STD_18"
                    onChange={(e) => {
                      clearFieldError('rateCode');
                      setForm((f) => ({ ...f, rateCode: e.target.value.toUpperCase() }));
                    }}
                  />
                  {getFieldError('rateCode') && <div className="form-error-message">{getFieldError('rateCode')}</div>}
                </div>

                <div className="form-group" data-field="name">
                  <label className="form-label">Name <span className="text-danger">*</span></label>
                  <input
                    className={`form-input${getFieldError('name') ? ' form-field-error' : ''}`}
                    value={form.name}
                    maxLength={120}
                    placeholder="Standard rate 18%"
                    onChange={(e) => { clearFieldError('name'); setForm((f) => ({ ...f, name: e.target.value })); }}
                  />
                  {getFieldError('name') && <div className="form-error-message">{getFieldError('name')}</div>}
                </div>

                <div className="form-group" data-field="supplyCategory">
                  <SyutSelect
                    label="Category"
                    options={categories}
                    value={form.supplyCategory}
                    onChange={(val) => applyCategory((val as string) || 'STANDARD')}
                    isClearable={false}
                  />
                </div>
              </div>
            )}

            {/* Row 2 — effective date, references */}
            <div className={isNewMaster ? 'form-grid-2' : 'form-grid-3'}>
              <div className="form-group" data-field="effectiveFrom">
                <label className="form-label">Effective from <span className="text-danger">*</span></label>
                <input
                  type="date"
                  className={`form-input${getFieldError('effectiveFrom') ? ' form-field-error' : ''}`}
                  value={form.effectiveFrom}
                  onChange={(e) => {
                    clearFieldError('effectiveFrom');
                    setForm((f) => ({ ...f, effectiveFrom: e.target.value }));
                  }}
                />
                {getFieldError('effectiveFrom')
                  ? <div className="form-error-message">{getFieldError('effectiveFrom')}</div>
                  : <div className="form-hint">Applies to documents dated on or after this day.</div>}
              </div>

              <div className="form-group" data-field="notificationReference">
                <label className="form-label">Notification reference</label>
                <input
                  className="form-input"
                  value={form.notificationReference}
                  maxLength={200}
                  placeholder="Notification 9/2025, Schedule I, Sl. 34"
                  onChange={(e) => setForm((f) => ({ ...f, notificationReference: e.target.value }))}
                />
                <div className="form-hint">Optional. Cited at assessment.</div>
              </div>

              {!isNewMaster && (
                <div className="form-group" data-field="reason">
                  <label className="form-label">Reason</label>
                  <input
                    className="form-input"
                    value={form.reason}
                    maxLength={200}
                    placeholder="Why this version is being recorded"
                    onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                  />
                  <div className="form-hint">Recorded in the audit trail.</div>
                </div>
              )}
            </div>

            {/* Row 3 — rate shape: checkboxes on the input line, basis beside them */}
            <div className="form-grid-3">
              <div className="form-group">
                <label className="form-label">Rate shape</label>
                <label className={`txm-check-row${!isNewMaster || isNil ? ' is-disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={form.isSlab}
                    disabled={!isNewMaster || isNil}
                    onChange={(e) => {
                      clearFieldError('bands');
                      setForm((f) => ({
                        ...f,
                        isSlab: e.target.checked,
                        conditionKey: e.target.checked ? f.conditionKey : '',
                        // Turning slabs on gives band 1 its statutory start immediately.
                        bands: e.target.checked ? chainBands(f.bands) : f.bands,
                      }));
                    }}
                  />
                  <span>Slab — rate varies by value</span>
                </label>
                {isNil && <div className="form-hint">A nil rate does not vary by value.</div>}
              </div>

              <div className="form-group">
                <label className="form-label">Cess</label>
                <label className={`txm-check-row${isNil ? ' is-disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={form.withCess}
                    disabled={isNil}
                    onChange={(e) => setForm((f) => ({ ...f, withCess: e.target.checked }))}
                  />
                  <span>Include a cess component</span>
                </label>
              </div>

              <div className="form-group" data-field="conditionKey">
                <SyutSelect
                  label={<>Evaluates against {form.isSlab && <span className="text-danger">*</span>}</>}
                  options={basisOptions}
                  value={form.conditionKey}
                  onChange={(val) => { clearFieldError('conditionKey'); setForm((f) => ({ ...f, conditionKey: (val as string) || '' })); }}
                  placeholder={form.isSlab ? 'Select the published basis…' : 'Flat rate — not applicable'}
                  isDisabled={!form.isSlab || !isNewMaster}
                  isClearable
                  error={getFieldError('conditionKey')}
                />
                {form.isSlab && !getFieldError('conditionKey') && (
                  <div className="form-hint">The transaction fact the bands compare, e.g. the room tariff per night.</div>
                )}
              </div>
            </div>

            {/* Row 4 — rates: one grid row per band */}
            <div className="form-group" data-field="bands">
              <label className="form-label">Rates <span className="text-danger">*</span></label>
              <div className="txm-bands" style={{ ['--txm-band-cols' as string]: String(bandCols) }}>
                {form.bands.map((band, i) => (
                  <div key={i} className={`txm-band${form.isSlab ? '' : ' is-flat'}`}>
                    {form.isSlab && (
                      <div>
                        <label className="form-label">From</label>
                        {/* Derived, never typed — this is what makes a gap impossible. */}
                        <input className="form-input" value={band.valueFrom} readOnly tabIndex={-1}
                               title="Starts exactly where the band below ends. A value on the boundary is taxed at the lower band's rate." />
                      </div>
                    )}
                    {form.isSlab && (
                      <div>
                        <label className="form-label">Up to</label>
                        <input
                          className="form-input"
                          inputMode="decimal"
                          value={band.valueTo}
                          placeholder={i === form.bands.length - 1 ? 'No limit' : ''}
                          onChange={(e) => setBand(i, { valueTo: e.target.value })}
                        />
                      </div>
                    )}
                    {codes.map((code) => (
                      <div key={code}>
                        <label className="form-label">{code} %</label>
                        <input className="form-input" inputMode="decimal" value={band.rates[code] ?? ''}
                               disabled={isNil}
                               onChange={(e) => setRate(i, code, e.target.value)} />
                      </div>
                    ))}
                    {form.isSlab && (
                      <div>
                        {form.bands.length > 1 && (
                          <button type="button" className="btn-icon txm-band-remove" title="Remove band" onClick={() => removeBand(i)}>
                            <IconSystem.trash size={16} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {form.isSlab && (
                <div className="txm-band-actions">
                  <button type="button" className="btn btn-secondary" onClick={addBand}>
                    <IconSystem.add size={16} />
                    Add band
                  </button>
                  {!getFieldError('bands') && (
                    <span className="form-hint" style={{ margin: 0 }}>
                      Enter each band's upper limit; the next band starts where it ends. Leave the last limit blank for "and above".
                    </span>
                  )}
                </div>
              )}

              {getFieldError('bands') && <div className="form-error-message">{getFieldError('bands')}</div>}
            </div>
          </>
        );
      }}
    </EnterpriseFormModal>
  );
};

export default TaxMasterFormModal;
